import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, createRoutesFromElements } from 'react-router';
import { createHashRouter } from 'react-router-dom';

import RouteHandler from '/business/shared/components/router/RouteHandler';
import { businessFakeLoginFlag, developmentMode } from '/shared/util/Statics';
import { urlApplicationRoot } from '/business/shared/util/Statics';
import types from './layoutTypes';

import PageError from '/shared/components/PageError';
import ErrorRouteBoundary from '/shared/components/functional/ErrorRouteBoundary';
import lazyLoadRoute from '/shared/components/functional/lazyLoadRoute';
import RootLayout from './layouts/RootLayout';
import reduceAppRoutes from './reduceRouteImports';

const reportingRoutes = {
  ReportingPage: () => import('/business/shared/components/pages/reporting/ReportPage'),
};
const appRoutes = {
  ThirdPartyAssurancePage: () => import('/business/shared/components/pages/assurance/ThirdPartyAssurancePage'),
  ApplicationOverviewPage: () =>
    import('/business/shared/components/pages/application-overview/ApplicationOverviewPage'),
  AnnualUpdatesPage: () => import('/business/shared/components/pages/annual-updates/AnnualUpdatesPage'),
  QuestionPage: () => import('/business/shared/components/pages/question/QuestionPage'),
  ResultsPage: () => import('/business/shared/components/pages/results/ResultsPage'),
  PaymentPage: () => import('/business/shared/components/pages/payment/PaymentPageLoader'),
  DebugTransitionApplicationPage: () => import('/business/shared/components/debug/DebugTransitionApplicationPage'),
  OffsetPage: () => import('/business/shared/components/pages/confirmed-optional-combined/OffsetPageSwitcher'),
};
const authRoutes = {
  DashboardPage: () => import('/business/shared/components/pages/dashboard/DashboardPage'),
  ApplicationSettingsSysPrefs: () =>
    import('/business/shared/components/pages/application-settings/ApplicationSettingsSysPrefs'),
  ApplicationSettingsDealershipDetails: () =>
    import('/business/shared/components/pages/application-settings/ApplicationSettingsDealershipDetails'),
  ApplicationSettingsReportingPeriod: () =>
    import('/business/shared/components/pages/application-settings/ApplicationSettingsReportingPeriod'),
  ApplicationSettingsUserSettings: () =>
    import('/business/shared/components/pages/application-settings/ApplicationSettingsUserSettings'),
  ApplicationSettingsContribution: () =>
    import('/business/shared/components/pages/application-settings/ApplicationSettingsContribution'),
  MyProfile: () => import('/business/shared/components/pages/my-profile/ProfilePage'),
  CreateApplication: () => import('/business/shared/components/pages/create-application/CreateApplication'),
  CreateApplicationSuccess: () =>
    import('/business/shared/components/pages/create-application-success/CreateApplicationSuccess'),
};

const miscRoutes = {
  PrivacyPage: () => import('/business/shared/components/pages/misc/PrivacyPolicyPage'),
  CookiePage: () => import('/business/shared/components/pages/misc/CookiePage'),
  WelcomePage: () => import('/business/shared/components/pages/sso/WelcomePage'),
  UserInfoPage: () => import('/business/shared/components/pages/sso/UserInfoPage'),
  RegisterPage: () => import('/business/shared/components/pages/sso/RegisterPage'),
  RegisteredPage: () => import('/business/shared/components/pages/sso/RegisterPageCompleteAccount'),
  SSOLoginPage: () => import('/business/shared/components/pages/sso/SSOLoginPage'),
  SSOCallbackPage: () => import('/business/shared/components/pages/sso/SSOCallbackPage'),
  SSOErrorPage: () => import('/business/shared/components/pages/sso/SSOErrorPage'),
  SSOLogoutPage: () => import('/business/shared/components/pages/sso/SSOLogoutPage'),
  DebugLoginPage: () => import('/shared/components/FakeLoginPage'),
  InviteHandlerPage: () => import('/business/shared/components/pages/invite-handler/InviteHandler'),
  InviteDeclinedPage: () => import('/business/shared/components/pages/invite-feedback/InviteDeclinedPage'),
};

type CoreRouterConfig = {
  hasContribution?: boolean;
};

function useGetCoreRouter(config: CoreRouterConfig) {
  const { hasContribution } = config;

  const { t } = useTranslation();

  return useMemo(
    () =>
      createHashRouter(
        createRoutesFromElements(
          <Route element={<RootLayout />} errorElement={<ErrorRouteBoundary />}>
            <Route
              path="/"
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.DashboardPage)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}-settings/business-details`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.ApplicationSettingsDealershipDetails)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}-settings/sys-prefs`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.ApplicationSettingsSysPrefs)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}-settings/reporting-period`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.ApplicationSettingsReportingPeriod)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}-settings/user-settings`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.ApplicationSettingsUserSettings)}
                />
              }
            />
            {hasContribution && (
              <Route
                path={`/${urlApplicationRoot}-settings/contribution`}
                element={
                  <RouteHandler
                    requiresAuth
                    requiresApplication
                    requiresCarbonCreditServices
                    renderLayout={types.AUTH}
                    component={lazyLoadRoute(authRoutes.ApplicationSettingsContribution)}
                  />
                }
              />
            )}
            <Route
              path={`/create-${urlApplicationRoot}`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.CreateApplication)}
                />
              }
            />
            <Route
              path={`/create-${urlApplicationRoot}/success`}
              element={
                <RouteHandler
                  requiresAuth
                  renderLayout={types.AUTH}
                  component={lazyLoadRoute(authRoutes.CreateApplicationSuccess)}
                />
              }
            />
            <Route
              path="/my-profile"
              element={
                <RouteHandler requiresAuth renderLayout={types.AUTH} component={lazyLoadRoute(authRoutes.MyProfile)} />
              }
            />

            <Route
              path={`/${urlApplicationRoot}/annual-updates`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresAuth
                  requiresApplication
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.AnnualUpdatesPage)}
                />
              }
            />

            <Route
              path={`/${urlApplicationRoot}`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresAuth
                  requiresApplication
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.ApplicationOverviewPage)}
                />
              }
            />

            <Route
              path={`/${urlApplicationRoot}/questions/*`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresApplication
                  requiresAuth
                  requiresAgreement
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.QuestionPage)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/results`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.ResultsPage)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/third-party-assurance`}
              element={
                <RouteHandler
                  requiresAuth
                  requiresApplication
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.ThirdPartyAssurancePage)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionNotOnboarded
                  requiresAuth
                  requiresApplication
                  redirectTo={`/${urlApplicationRoot}/reduce/onboarding`}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/onboarding`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionNotOnboarded
                  requiresAuth
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceOnboarding)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/comparison`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceComparison)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/hotspots`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceHotspots)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/targets`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceTargets)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/request-information`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceRequestInfo)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/initiatives`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReduceInitiatives)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/reduce/plan`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresReductionOnboarded
                  requiresAuth
                  requiresAgreement
                  requiresApplication
                  renderLayout={types.REDUCEV3}
                  component={lazyLoadRoute(reduceAppRoutes.ReducePlan)}
                />
              }
            />

            <Route
              path={`/${urlApplicationRoot}/offsets`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresAuth
                  requiresApplication
                  requiresCarbonCreditServices
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.OffsetPage)}
                />
              }
            />
            <Route
              path={`/${urlApplicationRoot}/payment`}
              handle={{ syncApplication: true }}
              element={
                <RouteHandler
                  requiresApplication
                  requiresAuth
                  requiresAgreement
                  requiresCarbonCreditServices
                  renderLayout={types.APP}
                  component={lazyLoadRoute(appRoutes.PaymentPage)}
                />
              }
            />
            {developmentMode && (
              <Route
                path={`/${urlApplicationRoot}/debug-transition-application`}
                handle={{ syncApplication: true }}
                element={
                  <RouteHandler
                    requiresApplication
                    requiresAuth
                    renderLayout={types.APP}
                    component={lazyLoadRoute(appRoutes.DebugTransitionApplicationPage)}
                  />
                }
              />
            )}
            <Route
              path="/sso/welcome"
              element={
                <RouteHandler hasFooter renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.WelcomePage)} />
              }
            />
            <Route
              path="/sso/user-info"
              element={
                <RouteHandler
                  requiresAuth
                  hasFooter
                  renderLayout={types.MISC}
                  component={lazyLoadRoute(miscRoutes.UserInfoPage)}
                />
              }
            />
            <Route
              path="/sso/register"
              element={
                <RouteHandler hasFooter renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.RegisterPage)} />
              }
            />
            <Route
              path="/sso/registered"
              element={
                <RouteHandler
                  hasFooter
                  renderLayout={types.MISC}
                  component={lazyLoadRoute(miscRoutes.RegisteredPage)}
                />
              }
            />
            <Route
              path="/sso/login"
              element={<RouteHandler renderLayout={types.EMPTY} component={lazyLoadRoute(miscRoutes.SSOLoginPage)} />}
            />
            <Route
              path="/sso/callback"
              element={<RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.SSOCallbackPage)} />}
            />
            <Route
              path="/sso/error"
              element={<RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.SSOErrorPage)} />}
            />
            <Route
              path="/sso/logout"
              element={
                <RouteHandler
                  hasFooterFixedToBottom
                  renderLayout={types.MISC}
                  component={lazyLoadRoute(miscRoutes.SSOLogoutPage)}
                />
              }
            />
            <Route
              path="/privacy-policy"
              element={<RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.PrivacyPage)} />}
            />
            <Route
              path="/cookie-preferences"
              element={<RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.CookiePage)} />}
            />
            {businessFakeLoginFlag && (
              <Route
                path="/fake-login"
                element={
                  <RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.DebugLoginPage)} />
                }
              />
            )}
            <Route
              path="/reporting"
              element={
                <RouteHandler
                  requiresAuth
                  requiresReportingAuth
                  renderLayout={types.AUTH_REPORT}
                  component={lazyLoadRoute(reportingRoutes.ReportingPage)}
                />
              }
            />
            <Route
              path="/invite/:inviteStatus/:appId/:inviteToken"
              element={
                <RouteHandler renderLayout={types.MISC} component={lazyLoadRoute(miscRoutes.InviteHandlerPage)} />
              }
            />
            <Route
              path="/invite-feedback/declined/:appId/:inviteToken"
              element={
                <RouteHandler
                  hasFooterFixedToBottom
                  renderLayout={types.MISC}
                  component={lazyLoadRoute(miscRoutes.InviteDeclinedPage)}
                />
              }
            />
            <Route path="*" element={<PageError error={t('shared:pageNotFound', 'This page could not be found')} />} />
          </Route>,
        ),
      ),
    [hasContribution, t],
  );
}

export default useGetCoreRouter;
